import * as THREE from 'three';
import type { BodyState } from '../astro/Universe';
import { FOREGROUND_ORDER } from './foreground';

// Short-lived visual effects anchored to a body frame (so frame dragging carries them along).

type Anchored = { anchor: BodyState; rel: THREE.Vector3 };

type Bolt = Anchored & { dir: THREE.Vector3; speed: number; life: number; travel: number; maxTravel: number; mesh: THREE.Mesh };
type Particle = Anchored & { vel: THREE.Vector3; life: number; maxLife: number; size: number; color: THREE.Color };
type Debris = Anchored & { vel: THREE.Vector3; life: number; axis: THREE.Vector3; spin: number; size: number; variant: number };
type Flash = Anchored & { life: number; maxLife: number; size: number; mesh: THREE.Mesh };

const MAX_PARTICLES = 1500;
const MAX_DEBRIS = 240;

let glowTex: THREE.Texture | null = null;
function glowTexture(): THREE.Texture {
  if (glowTex) return glowTex;
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.3, 'rgba(255,255,255,0.6)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  glowTex = new THREE.CanvasTexture(c);
  return glowTex;
}

const tmp = new THREE.Vector3();
const tmpM = new THREE.Matrix4();
const tmpQ = new THREE.Quaternion();
const tmpS = new THREE.Vector3();

export class Effects {
  readonly group = new THREE.Group();
  private readonly bolts: Bolt[] = [];
  private readonly boltPool: THREE.Mesh[] = [];
  private readonly particles: Particle[] = [];
  private readonly debris: Debris[] = [];
  private readonly flashes: Flash[] = [];
  private readonly pPositions = new Float32Array(MAX_PARTICLES * 3);
  private readonly pColors = new Float32Array(MAX_PARTICLES * 3);
  private readonly pSizes = new Float32Array(MAX_PARTICLES);
  private readonly points: THREE.Points;
  private debrisMeshes: THREE.InstancedMesh[] = [];
  private readonly boltGeo = new THREE.CylinderGeometry(0.18, 0.18, 1, 6, 1, true).rotateX(Math.PI / 2);

  constructor() {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.pPositions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(this.pColors, 3));
    geo.setAttribute('size', new THREE.BufferAttribute(this.pSizes, 1));
    this.points = new THREE.Points(
      geo,
      new THREE.ShaderMaterial({
        uniforms: { map: { value: glowTexture() }, scale: { value: 600 } },
        vertexShader: /* glsl */ `
          #include <common>
          #include <logdepthbuf_pars_vertex>
          attribute vec3 color;
          attribute float size;
          uniform float scale;
          varying vec3 vColor;
          void main() {
            vColor = color;
            vec4 mv = modelViewMatrix * vec4(position, 1.0);
            gl_Position = projectionMatrix * mv;
            gl_PointSize = clamp(size * scale / max(-mv.z, 0.001), 0.0, 256.0);
            #include <logdepthbuf_vertex>
          }`,
        fragmentShader: /* glsl */ `
          #include <logdepthbuf_pars_fragment>
          uniform sampler2D map;
          varying vec3 vColor;
          void main() {
            #include <logdepthbuf_fragment>
            float a = texture2D(map, gl_PointCoord).r;
            gl_FragColor = vec4(vColor * a, 1.0);
          }`,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        transparent: true,
      }),
    );
    this.points.frustumCulled = false;
    this.points.renderOrder = FOREGROUND_ORDER + 2;
    this.group.add(this.points);
  }

  /** Use the rock variant geometries for debris chunks. */
  setDebrisSources(meshes: { geometry: THREE.BufferGeometry; material: THREE.Material }[]): void {
    this.debrisMeshes = meshes.map((m) => {
      const im = new THREE.InstancedMesh(m.geometry, m.material, MAX_DEBRIS);
      im.count = 0;
      im.frustumCulled = false;
      im.renderOrder = FOREGROUND_ORDER;
      this.group.add(im);
      return im;
    });
  }

  laser(anchor: BodyState, from: THREE.Vector3, dir: THREE.Vector3, color: THREE.Color, maxTravel: number): void {
    let mesh = this.boltPool.pop();
    if (!mesh) {
      mesh = new THREE.Mesh(
        this.boltGeo,
        new THREE.MeshBasicMaterial({ color, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false }),
      );
      mesh.renderOrder = FOREGROUND_ORDER + 1;
      mesh.frustumCulled = false;
    }
    (mesh.material as THREE.MeshBasicMaterial).color.copy(color).multiplyScalar(4);
    this.group.add(mesh);
    this.bolts.push({
      anchor, rel: from.clone().sub(anchor.position), dir: dir.clone(), speed: 9000, life: 0, travel: 0,
      maxTravel: Math.max(maxTravel, 60), mesh,
    });
    this.burst(anchor, from, 4, color, 1.2, 40, 0.12);
  }

  burst(anchor: BodyState, at: THREE.Vector3, count: number, color: THREE.Color, size: number, speed: number, life: number, baseVel?: THREE.Vector3): void {
    for (let i = 0; i < count && this.particles.length < MAX_PARTICLES; i++) {
      const v = new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize().multiplyScalar(speed * (0.3 + Math.random()));
      if (baseVel) v.add(baseVel);
      const l = life * (0.5 + Math.random() * 0.8);
      this.particles.push({ anchor, rel: at.clone().sub(anchor.position), vel: v, life: l, maxLife: l, size: size * (0.6 + Math.random() * 0.8), color });
    }
  }

  shatter(anchor: BodyState, at: THREE.Vector3, radius: number, variant: number): void {
    const n = Math.min(26, 8 + Math.floor(radius / 3));
    for (let i = 0; i < n && this.debris.length < MAX_DEBRIS; i++) {
      const dir = new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize();
      this.debris.push({
        anchor, rel: at.clone().sub(anchor.position).addScaledVector(dir, radius * 0.5 * Math.random()),
        vel: dir.multiplyScalar(radius * (0.6 + Math.random() * 1.5)),
        life: 3 + Math.random() * 2, axis: new THREE.Vector3(Math.random(), Math.random(), Math.random()).normalize(),
        spin: (Math.random() - 0.5) * 6, size: radius * (0.08 + Math.random() * 0.18), variant,
      });
    }
    const dust = new THREE.Color(0.75, 0.68, 0.6);
    this.burst(anchor, at, 60, dust, radius * 0.35, radius * 1.2, 1.4);
    this.burst(anchor, at, 30, new THREE.Color(1.0, 0.6, 0.25), radius * 0.15, radius * 2, 0.5);
    this.flash(anchor, at, radius * 3, 0.35, new THREE.Color(1, 0.75, 0.45));
  }

  explosion(anchor: BodyState, at: THREE.Vector3, size: number): void {
    this.flash(anchor, at, size * 6, 0.6, new THREE.Color(1, 0.8, 0.5));
    this.burst(anchor, at, 160, new THREE.Color(1.0, 0.55, 0.2), size * 0.4, size * 4, 1.6);
    this.burst(anchor, at, 80, new THREE.Color(0.9, 0.9, 1.0), size * 0.2, size * 7, 0.6);
    this.burst(anchor, at, 60, new THREE.Color(0.35, 0.33, 0.32), size * 0.8, size * 1.5, 3);
  }

  flash(anchor: BodyState, at: THREE.Vector3, size: number, life: number, color: THREE.Color): void {
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: { map: { value: glowTexture() }, color: { value: color.clone().multiplyScalar(5) }, alpha: { value: 1 } },
        vertexShader: `varying vec2 vUv; void main(){ vUv=uv; vec4 mv = modelViewMatrix*vec4(0.,0.,0.,1.); vec2 s=vec2(length(modelMatrix[0].xyz), length(modelMatrix[1].xyz)); mv.xy += position.xy*s; gl_Position=projectionMatrix*mv; gl_Position.z = -gl_Position.w * 0.999; }`,
        fragmentShader: `uniform sampler2D map; uniform vec3 color; uniform float alpha; varying vec2 vUv; void main(){ gl_FragColor=vec4(color*texture2D(map,vUv).r*alpha,1.0); }`,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false,
        transparent: true,
      }),
    );
    mesh.frustumCulled = false;
    mesh.renderOrder = FOREGROUND_ORDER + 3;
    this.group.add(mesh);
    this.flashes.push({ anchor, rel: at.clone().sub(anchor.position), life, maxLife: life, size, mesh });
  }

  update(dt: number, origin: THREE.Vector3): void {
    // Bolts
    for (let i = this.bolts.length - 1; i >= 0; i--) {
      const b = this.bolts[i];
      const step = b.speed * dt;
      b.travel += step;
      b.rel.addScaledVector(b.dir, step);
      if (b.travel > b.maxTravel) {
        this.group.remove(b.mesh);
        this.boltPool.push(b.mesh);
        this.bolts.splice(i, 1);
        continue;
      }
      const len = Math.min(160, b.travel);
      b.mesh.position.copy(b.anchor.position).add(b.rel).addScaledVector(b.dir, -len / 2).sub(origin);
      b.mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), b.dir);
      b.mesh.scale.set(4, 4, len);
    }
    // Particles
    let n = 0;
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      p.rel.addScaledVector(p.vel, dt);
      p.vel.multiplyScalar(Math.exp(-0.6 * dt));
      tmp.copy(p.anchor.position).add(p.rel).sub(origin);
      const k = p.life / p.maxLife;
      this.pPositions.set([tmp.x, tmp.y, tmp.z], n * 3);
      this.pColors.set([p.color.r * k, p.color.g * k, p.color.b * k], n * 3);
      this.pSizes[n] = p.size;
      n++;
    }
    const geo = this.points.geometry;
    geo.setDrawRange(0, n);
    geo.attributes.position.needsUpdate = true;
    geo.attributes.color.needsUpdate = true;
    geo.attributes.size.needsUpdate = true;
    // Debris
    const counts = this.debrisMeshes.map(() => 0);
    for (let i = this.debris.length - 1; i >= 0; i--) {
      const d = this.debris[i];
      d.life -= dt;
      if (d.life <= 0) {
        this.debris.splice(i, 1);
        continue;
      }
      d.rel.addScaledVector(d.vel, dt);
      const im = this.debrisMeshes[d.variant % Math.max(1, this.debrisMeshes.length)];
      if (!im) continue;
      tmp.copy(d.anchor.position).add(d.rel).sub(origin);
      tmpQ.setFromAxisAngle(d.axis, d.spin * d.life);
      tmpS.setScalar(d.size * Math.min(1, d.life));
      tmpM.compose(tmp, tmpQ, tmpS);
      im.setMatrixAt(counts[d.variant % this.debrisMeshes.length]++, tmpM);
    }
    this.debrisMeshes.forEach((im, i) => {
      im.count = counts[i];
      im.instanceMatrix.needsUpdate = true;
    });
    // Flashes
    for (let i = this.flashes.length - 1; i >= 0; i--) {
      const f = this.flashes[i];
      f.life -= dt;
      if (f.life <= 0) {
        this.group.remove(f.mesh);
        f.mesh.geometry.dispose();
        (f.mesh.material as THREE.Material).dispose();
        this.flashes.splice(i, 1);
        continue;
      }
      const k = f.life / f.maxLife;
      f.mesh.position.copy(f.anchor.position).add(f.rel).sub(origin);
      f.mesh.scale.setScalar(f.size * (1.4 - k * 0.4));
      ((f.mesh.material as THREE.ShaderMaterial).uniforms.alpha.value = k * k);
    }
  }
}
